/**
 * @file out-of-stock-report.tsx
 * @module app/modules/finance-reports/out-of-stock-report
 * @description Displays out-of-stock product report data with quantity labels, filters, and pagination.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner, ProgressBar } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { KTSVG } from "../../../_admin/helpers";
import APICallService from "../../../api/apiCallService";
import { buyer, reports } from "../../../api/apiEndPoints";
import Loader from "../../../Global/loader";
import SimplePagination from "../../../Global/simple-pagination";
import {
  OutOfStockReportPermission,
  Download,
  PAGE_LIMIT,
  View,
  AllCustomers,
  ORDER_PAGE_LIMIT,
  CartonWithDozens,
  Dozen,
  Piece,
  CartonWithPieces,
} from "../../../utils/constants";
import b1 from "../../../_admin/assets/media/product/default.svg";
import Method from "../../../utils/methods";
import { useAuth } from "../auth";
import { getKey, setKey } from "../../../Global/history";
import { IGetCustomers } from "../../../types/responseIndex";
import clsx from "clsx";
import OrderPagination from "../../../Global/order-pagination";
import PermissionModal from "../../modals/permission";
import { useWarehouse } from "../../context/WarehouseContext";
import { CustomSelect } from "../../custom/Select/CustomSelect";
import { CustomComponentSelect } from "../../custom/Select/CustomComponentSelect";
import CustomDatePicker from "../../custom/DateRange/DatePicker";
import { master } from "../../../api/apiEndPoints";
import { useDebounce } from "../../../utils/useDebounce";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import ImageWithFallback from "../_common/components/image-with-fallback";

const getQtyLabel = (type: number) => {
  switch (type) {
    case CartonWithDozens:
      return "Carton with Dozens";
    case Dozen:
      return "Dozen";
    case Piece:
      return "Piece";
    case CartonWithPieces:
      return "Carton with Pieces";
    default:
      return "N/A";
  }
};

const isValidDate = (d: any) => d instanceof Date && !isNaN(d.getTime());

const sanitizeForStorage = (val: any): any => {
  if (!val || typeof val !== "object") return val;
  if (Array.isArray(val)) return val.map(sanitizeForStorage);

  const { label, ...rest } = val;
  const sanitized: any = { ...rest };
  Object.keys(sanitized).forEach((key) => {
    if (sanitized[key] && typeof sanitized[key] === "object") {
      sanitized[key] = sanitizeForStorage(sanitized[key]);
    }
  });
  return sanitized;
};

const getCategoryLabel = (val: any) => (
  <div className="d-flex align-items-center">
    <div className="symbol symbol-30px border me-2">
      <ImageWithFallback src={val.image || b1} className="object-fit-contain" alt="" fallbackSrc={b1} noDefaultSize />
    </div>
    <span className="fs-15 fw-600">{val.name || val.title}</span>
  </div>
);

const getSubCategoryLabel = (val: any) => (
  <div className="d-flex align-items-center">
    <span className="fs-15 fw-600">{val.name || val.title}</span>
  </div>
);

const getBrandLabel = (val: any) => (
  <div className="d-flex align-items-center">
    <div className="symbol symbol-30px border me-2">
      <ImageWithFallback src={val.image || b1} className="object-fit-contain" alt="" fallbackSrc={b1} noDefaultSize />
    </div>
    <span className="fs-15 fw-600">{val.name || val.title}</span>
  </div>
);

const OutOfStockReport = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const OOS_KEYS = {
    page: "oos_report_page",
    limit: "oos_report_limit",
    category: "oos_report_category",
    subCategory: "oos_report_subCategory",
    groupCategory: "oos_report_groupCategory",
    brand: "oos_report_brand",
    startDate: "oos_report_startDate",
    endDate: "oos_report_endDate",
    sortKey: "oos_report_sortKey",
    sortOrder: "oos_report_sortOrder",
    searchTerm: "oos_report_searchTerm",
  };

  const getStoredJson = (key: string) => {
    const val = getKey(key);
    if (!val || typeof val !== "string") return null;
    try {
      return JSON.parse(val);
    } catch {
      return null;
    }
  };

  const getDefaultDates = () => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    return { start, end };
  };
  const { start: defaultStart, end: defaultEnd } = getDefaultDates();

  const storedCategoryRaw = getStoredJson(OOS_KEYS.category);
  const storedSubCategoryRaw = getStoredJson(OOS_KEYS.subCategory);
  const storedGroupCategoryRaw = getStoredJson(OOS_KEYS.groupCategory);
  const storedBrandRaw = getStoredJson(OOS_KEYS.brand);

  const storedCategory = storedCategoryRaw
    ? {
        ...storedCategoryRaw,
        label: getCategoryLabel(storedCategoryRaw),
        subCategories: (storedCategoryRaw.subCategories || []).map(
          (sub: any) => ({
            ...sub,
            label: getSubCategoryLabel(sub),
            groupCategories: (sub.groupCategories || []).map((group: any) => ({
              ...group,
              label: getSubCategoryLabel(group),
            })),
          }),
        ),
      }
    : null;

  const storedSubCategory = storedSubCategoryRaw
    ? {
        ...storedSubCategoryRaw,
        label: getSubCategoryLabel(storedSubCategoryRaw),
        groupCategories: (storedSubCategoryRaw.groupCategories || []).map(
          (group: any) => ({
            ...group,
            label: getSubCategoryLabel(group),
          }),
        ),
      }
    : null;

  const storedGroupCategory = storedGroupCategoryRaw
    ? {
        ...storedGroupCategoryRaw,
        label: getSubCategoryLabel(storedGroupCategoryRaw),
      }
    : null;

  const storedBrand = storedBrandRaw
    ? {
        ...storedBrandRaw,
        label: getBrandLabel(storedBrandRaw),
      }
    : null;

  const storedStartDateVal = getStoredJson(OOS_KEYS.startDate);
  const storedEndDateVal = getStoredJson(OOS_KEYS.endDate);

  const parseStoredDate = (val: any, fallback: Date) => {
    if (!val) return fallback;
    const d = new Date(val);
    return isValidDate(d) ? d : fallback;
  };

  const storedStartDate = parseStoredDate(storedStartDateVal, defaultStart);
  const storedEndDate = parseStoredDate(storedEndDateVal, defaultEnd);
  const storedPageVal = getStoredJson(OOS_KEYS.page);
  const storedPage = storedPageVal ? Number(storedPageVal) : 1;
  const storedLimitVal = getStoredJson(OOS_KEYS.limit);
  const storedLimit = storedLimitVal ? Number(storedLimitVal) : PAGE_LIMIT;
  const storedSortKey = getStoredJson(OOS_KEYS.sortKey) || "title";
  const storedSortOrderVal = getStoredJson(OOS_KEYS.sortOrder);
  const storedSortOrder = storedSortOrderVal ? Number(storedSortOrderVal) : 1;
  const storedSearchTerm = getStoredJson(OOS_KEYS.searchTerm) || "";

  const [fetchLoading, setFetchLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(storedPage);
  const [pageLimit, setPageLimit] = useState(storedLimit);
  const [reportData, setReportData] = useState<IGetCustomers | any>([]);
  const [searchTerm, setSearchTerm] = useState(storedSearchTerm);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(storedCategory);
  const [selectedSubCategory, setSelectedSubCategory] =
    useState<any>(storedSubCategory);
  const [subCategories, setSubCategories] = useState<any>(
    storedCategory?.subCategories || [],
  );
  const [selectedGroupCategory, setSelectedGroupCategory] =
    useState<any>(storedGroupCategory);
  const [groupCategories, setGroupCategories] = useState<any>(
    storedSubCategory?.groupCategories || [],
  );
  const [selectedBrand, setSelectedBrand] = useState<any>(storedBrand);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [sortKey, setSortKey] = useState(storedSortKey);
  const [sortOrder, setSortOrder] = useState(storedSortOrder);
  const [startDate, setStartDate] = useState<any>(storedStartDate);
  const [endDate, setEndDate] = useState<any>(storedEndDate);
  const [categories, setCategories] = useState<any>([]);
  const [brands, setBrands] = useState<any>([]);
  const [fetchCategoryLoader, setFetchCategoryLoader] = useState(false);
  const [fetchBrandLoader, setFetchBrandLoader] = useState(false);
  const [categoryPage, setCategoryPage] = useState(1);
  const [brandPage, setBrandPage] = useState(1);
  const [totalCategory, setTotalCategory] = useState(0);
  const [totalBrand, setTotalBrand] = useState(0);
  const [categorySearch, setCategorySearch] = useState("");
  const [brandSearch, setBrandSearch] = useState("");
  const isAllowedToDownload = Method.hasPermission(
    OutOfStockReportPermission,
    Download,
    currentUser,
  );

  useEffect(() => {
    if (!Method.hasPermission(OutOfStockReportPermission, View, currentUser)) {
      window.history.back();
    }
  }, []);

  useEffect(() => {
    if (selectedWarehouse?.value) {
      (async () => {
        setFetchLoading(true);
        await fetchReportData(
          page,
          pageLimit,
          searchTerm,
          sortKey,
          sortOrder,
          selectedCategory,
          selectedSubCategory,
          selectedGroupCategory,
          selectedBrand,
          startDate,
          endDate,
        );
        await fetchCategory(1, PAGE_LIMIT);
        await fetchBrands(1, PAGE_LIMIT);
        setFetchLoading(false);
      })();
    } else {
      setFetchLoading(false);
    }
  }, [selectedWarehouse?.value]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isExporting) {
        e.preventDefault();
        e.returnValue =
          "Export is in progress. Are you sure you want to cancel and leave?";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isExporting]);

  const fetchCategory = async (
    page: number,
    limit: number,
    search: string = "",
  ) => {
    setFetchCategoryLoader(true);
    let params: any = {
      pageNo: page,
      limit: limit,
      searchTerm: search,
      sortKey: "title",
      sortOrder: 1,
      needCount: true,
      state: 1,
      categoriesDepth: 3,
    };
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    let apiService = new APICallService(
      master.categoryList,
      params,
      "",
      "",
      "",
      "",
      OutOfStockReportPermission,
      selectedWarehouse?.value,
    );
    let response = await apiService.callAPI();
    if (response?.records) {
      const formattedCategories = response.records.map((val: any) => ({
        value: val._id,
        name: val.title,
        id: val._id,
        image: val.image,
        label: getCategoryLabel(val),
        title:
          val.title +
          " " +
          (val.categories?.map((c: any) => c.title).join(" ") || ""),
        subCategories:
          val.categories?.map((sub: any) => ({
            value: sub._id,
            name: sub.title,
            id: sub._id,
            label: getSubCategoryLabel(sub),
            title: sub.title,
            groupCategories:
              sub.categories?.map((group: any) => ({
                value: group._id,
                name: group.title,
                id: group._id,
                label: getSubCategoryLabel(group),
                title: group.title,
              })) || [],
          })) || [],
      }));
      setTotalCategory(response.total);
      setCategories((prev: any) =>
        page === 1 ? formattedCategories : [...prev, ...formattedCategories],
      );
    } else {
      if (page === 1) setCategories([]);
    }
    setFetchCategoryLoader(false);
  };

  const fetchBrands = async (
    page: number,
    limit: number,
    search: string = "",
  ) => {
    setFetchBrandLoader(true);
    let params: any = {
      pageNo: page,
      limit: limit,
      searchTerm: search,
      sortKey: "title",
      sortOrder: 1,
      needCount: true,
    };
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    let apiService = new APICallService(
      master.listBrands,
      params,
      "",
      "",
      "",
      "",
      OutOfStockReportPermission,
      selectedWarehouse?.value,
    );
    let response = await apiService.callAPI();
    if (response?.records) {
      const formattedBrands = response.records.map((val: any) => ({
        value: val._id,
        id: val._id,
        image: val.image,
        label: getBrandLabel(val),
        title: val.title,
      }));
      setTotalBrand(response.total);
      setBrands((prev: any) =>
        page === 1 ? formattedBrands : [...prev, ...formattedBrands],
      );
    }
    setFetchBrandLoader(false);
  };

  const fetchReportData = async (
    pageNo: number,
    limit: number,
    search: string,
    sortField: string = sortKey,
    sortDirection: number = sortOrder,
    cat: any = selectedCategory,
    subCat: any = selectedSubCategory,
    groupCat: any = selectedGroupCategory,
    brand: any = selectedBrand,
    from: any = startDate,
    to: any = endDate,
  ) => {
    setLoading(true);
    let params: any = {
      skip: (pageNo - 1) * limit,
      limit: limit,
      sortKey: sortField,
      sortOrder: sortDirection,
      download: false,
    };

    if (search !== undefined && search !== null) {
      params.searchTerm = search;
    }

    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse.value;
    }

    if (cat?.id) {
      params.category = cat.id;
    }

    if (subCat?.id) {
      params.subCategory = subCat.id;
    }

    if (groupCat?.id) {
      params.groupCategory = groupCat.id;
    }

    if (brand?.id) {
      params.brand = brand.id;
    }
    console.log("Fetching with params:", params);

    if (from && to) {
      params.startDate = Method.convertDateToFormat(from, "YYYY-MM-DD");
      params.endDate = Method.convertDateToFormat(to, "YYYY-MM-DD");
      const diffTime = Math.abs(
        new Date(to).getTime() - new Date(from).getTime(),
      );
      params.totalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    }

    let apiService = new APICallService(
      reports.outOfStockReport,
      params,
      "",
      "",
      "",
      "",
      OutOfStockReportPermission,
      selectedWarehouse?.value,
    );
    let response = await apiService.callAPI();

    if (response) {
      const records = response?.records || [];

      if (records.length === 0 && pageNo > 1) {
        setLoading(false);
        return false;
      }

      if (typeof response?.total === "number") {
        setTotalRecords(response.total);
      } else if (pageNo === 1) {
        setTotalRecords(0);
      }

      setReportData(records);
      setLoading(false);
      return true;
    } else {
      if (pageNo === 1) {
        setReportData([]);
        setTotalRecords(0);
      }
      setLoading(false);
      return false;
    }
  };

  const handleCurrentPage = async (val: number) => {
    setLoading(true);
    const success = await fetchReportData(
      val,
      pageLimit,
      searchTerm,
      sortKey,
      sortOrder,
      selectedCategory,
      selectedSubCategory,
      selectedGroupCategory,
      selectedBrand,
      startDate,
      endDate,
    );

    if (success) {
      setPage(val);
      setKey(OOS_KEYS.page, val);
    } else if (val > 1) {
      // If next page attempt returned no data, assume we're at the end
      setTotalRecords((val - 1) * pageLimit);
    }
    setLoading(false);
  };

  const handleCategoryFilter = async (value: any) => {
    setSelectedCategory(value);
    setSubCategories(value?.subCategories || []);
    setSelectedSubCategory(null);
    setGroupCategories([]);
    setSelectedGroupCategory(null);
    setPage(1);
    const sanitizedRange = sanitizeForStorage(value);
    setKey(OOS_KEYS.category, sanitizedRange);
    setKey(OOS_KEYS.subCategory, null);
    setKey(OOS_KEYS.groupCategory, null);
    setKey(OOS_KEYS.page, 1);
    setLoading(true);
    await fetchReportData(
      1,
      pageLimit,
      searchTerm,
      sortKey,
      sortOrder,
      value,
      null,
      null,
      selectedBrand,
      startDate,
      endDate,
    );
    setLoading(false);
  };

  const handleSubCategoryFilter = async (value: any) => {
    setSelectedSubCategory(value);
    setGroupCategories(value?.groupCategories || []);
    setSelectedGroupCategory(null);
    setPage(1);
    const sanitizedRange = sanitizeForStorage(value);
    setKey(OOS_KEYS.subCategory, sanitizedRange);
    setKey(OOS_KEYS.groupCategory, null);
    setKey(OOS_KEYS.page, 1);
    setLoading(true);
    await fetchReportData(
      1,
      pageLimit,
      searchTerm,
      sortKey,
      sortOrder,
      selectedCategory,
      value,
      null,
      selectedBrand,
      startDate,
      endDate,
    );
    setLoading(false);
  };

  const handleGroupCategoryFilter = async (value: any) => {
    setSelectedGroupCategory(value);
    setPage(1);
    const sanitizedRange = sanitizeForStorage(value);
    setKey(OOS_KEYS.groupCategory, sanitizedRange);
    setKey(OOS_KEYS.page, 1);
    setLoading(true);
    await fetchReportData(
      1,
      pageLimit,
      searchTerm,
      sortKey,
      sortOrder,
      selectedCategory,
      selectedSubCategory,
      value,
      selectedBrand,
      startDate,
      endDate,
    );
    setLoading(false);
  };

  const handleBrandFilter = async (value: any) => {
    setSelectedBrand(value);
    setPage(1);
    const sanitizedRange = sanitizeForStorage(value);
    setKey(OOS_KEYS.brand, sanitizedRange);
    setKey(OOS_KEYS.page, 1);
    setLoading(true);
    await fetchReportData(
      1,
      pageLimit,
      searchTerm,
      sortKey,
      sortOrder,
      selectedCategory,
      selectedSubCategory,
      selectedGroupCategory,
      value,
      startDate,
      endDate,
    );
    setLoading(false);
  };

  const handleDateChange = async (dates: any) => {
    let [start, end] = dates || [null, null];
    let finalStart = start;
    let finalEnd = end;

    if (!start && !end) {
      const { start: dStart, end: dEnd } = getDefaultDates();
      finalStart = dStart;
      finalEnd = dEnd;
    } else if (start && end) {
      const maxAllowed = new Date(start);
      maxAllowed.setMonth(maxAllowed.getMonth() + 3);
      if (end > maxAllowed) {
        finalEnd = maxAllowed;
      }
    }

    setStartDate(finalStart);
    setEndDate(finalEnd);
    setKey(OOS_KEYS.startDate, finalStart);
    setKey(OOS_KEYS.endDate, finalEnd);

    if (finalStart && finalEnd) {
      setPage(1);
      setKey(OOS_KEYS.page, 1);
      setLoading(true);
      await fetchReportData(
        1,
        pageLimit,
        searchTerm,
        sortKey,
        sortOrder,
        selectedCategory,
        selectedSubCategory,
        selectedGroupCategory,
        selectedBrand,
        finalStart,
        finalEnd,
      );
      setLoading(false);
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "1":
        return "Cash";
      case "2":
        return "Tigo Pesa";
      case "3":
        return "MPesa";
      case "4":
        return "Card";
      case "5":
        return "DTB Bank";
      case "6":
        return "CRDB Bank";
      case "7":
        return "NMB Bank";
      case "8":
        return "Stanbic Bank";
      case "9":
        return "Airtel Money";
      case "10":
        return "Halo Pesa";
      default:
        return "Unknown";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "1":
        return "Pending";
      case "2":
        return "Completed";
      case "3":
        return "Cancelled";
      default:
        return "Unknown";
    }
  };
  const handleSort = async (field: string) => {
    let newSortOrder = 1;
    if (sortKey === field) {
      newSortOrder = sortOrder === 1 ? -1 : 1;
    }

    setSortKey(field);
    setSortOrder(newSortOrder);
    setPage(1);
    setKey(OOS_KEYS.sortKey, field);
    setKey(OOS_KEYS.sortOrder, newSortOrder);
    setKey(OOS_KEYS.page, 1);

    await fetchReportData(
      1,
      pageLimit,
      searchTerm,
      field,
      newSortOrder,
      selectedCategory,
      selectedSubCategory,
      selectedGroupCategory,
      selectedBrand,
      startDate,
      endDate,
    );
  };

  const debouncedFetch = useDebounce(fetchReportData, 500);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
    setKey(OOS_KEYS.page, 1);
    setKey(OOS_KEYS.searchTerm, value);
    debouncedFetch(
      1,
      pageLimit,
      value,
      sortKey,
      sortOrder,
      selectedCategory,
      selectedSubCategory,
      selectedGroupCategory,
      selectedBrand,
      startDate,
      endDate,
    );
  };

  const getSortIcon = (field: string) => {
    if (sortKey !== field) {
      return (
        <KTSVG
          path="/media/icons/duotune/arrows/arr003.svg"
          className="svg-icon-2 svg-icon-gray-400 ms-1"
        />
      );
    }

    return sortOrder === 1 ? (
      <KTSVG
        path="/media/icons/duotune/arrows/arr004.svg"
        className="svg-icon-2 svg-icon-gray-400 ms-1"
      />
    ) : (
      <KTSVG
        path="/media/icons/duotune/arrows/arr003.svg"
        className="svg-icon-2 svg-icon-gray-400 ms-1"
      />
    );
  };

  const getQtyLabelForExcelExport = (type: number) => {
    switch (type) {
      case CartonWithDozens:
        return "CartonWithDozens";
      case Dozen:
        return "Dozen";
      case Piece:
        return "Piece";
      case CartonWithPieces:
        return "CartonWithPieces";
      default:
        return "N/A";
    }
  };

  const exportRef = React.useRef<number>(0);

  const handleExportExcel = async () => {
    const runId = ++exportRef.current;

    // Create a toast
    const toastId = toast.loading("Initializing export...");

    const EXPORT_LIMIT = 10;

    const sanitizedParams: any = {
      searchTerm,
      sortKey,
      sortOrder,
      download: true,
    };

    if (selectedWarehouse?.value) {
      sanitizedParams.warehouse = selectedWarehouse.value;
    }
    if (selectedCategory?.id) {
      sanitizedParams.category = selectedCategory.id;
    }
    if (selectedSubCategory?.id) {
      sanitizedParams.subCategory = selectedSubCategory.id;
    }
    if (selectedGroupCategory?.id) {
      sanitizedParams.groupCategory = selectedGroupCategory.id;
    }
    if (selectedBrand?.id) {
      sanitizedParams.brand = selectedBrand.id;
    }
    if (startDate && endDate) {
      sanitizedParams.startDate = Method.convertDateToFormat(
        startDate,
        "YYYY-MM-DD",
      );
      sanitizedParams.endDate = Method.convertDateToFormat(
        endDate,
        "YYYY-MM-DD",
      );
      const diffTime = Math.abs(
        new Date(endDate).getTime() - new Date(startDate).getTime(),
      );
      sanitizedParams.totalDays =
        Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    }

    try {
      setIsExporting(true);

      if (runId !== exportRef.current) {
        toast.dismiss(toastId);
        return;
      }

      toast.update(toastId, {
        render: "Pre-calculating data range...",
        type: "info",
        isLoading: true,
      });

      let countParams = { ...sanitizedParams, skip: 0, limit: EXPORT_LIMIT };

      let countApiService = new APICallService(
        reports.outOfStockReport,
        countParams,
        "",
        "",
        "",
        "",
        OutOfStockReportPermission,
        selectedWarehouse?.value,
      );

      let countResponse = await countApiService.callAPI();

      if (runId !== exportRef.current) return;

      if (!countResponse || countResponse === 0) {
        throw new Error("Connection lost or API error occurred during export.");
      }

      const total =
        countResponse && typeof countResponse.total === "number"
          ? countResponse.total
          : null;

      if (total === 0) {
        toast.dismiss(toastId);
        toast.error("No records found to export", {
          autoClose: false,
          closeOnClick: false,
          className: "toast-center-icon",
        });
        return;
      }

      const headers = [
        "VendorName",
        "Product",
        "PackType",
        "StockCount",
        "PurchaseAmount",
        "Warehouse",
        "Brand",
        "Category",
        "Blocked Stock",
        "Total Warehouse Stock",
        "Qty Sold",
        "Sales Value (TSh)",
        "ATS",
        "Stock Cover Days",
        "OOS Days",
        "Days in stock",
        "Revenue Loss (TSh)",
        "Last Restock Date",
        "Last Restock Qty",
        "Order Needed",
      ];
      const workbook = XLSX.utils.book_new();
      const worksheetData: any[] = [headers];

      const rowMapper = (item: any) => [
        item?.VendorName,
        item?.title,
        getQtyLabelForExcelExport(item?.PackType),
        item?.StockCount,
        item?.PurchaseAmount,
        item?.Warehouse,
        (typeof item?.brand === "object" ? item?.brand?.title : item?.brand) ||
          "N/A",
        (typeof item?.category === "object"
          ? item?.category?.title
          : item?.category) || "N/A",
        item?.blockedStock || 0,
        item?.totalWarehouseStock || 0,
        item?.qtySold || 0,
        item?.salesValue || 0,
        item?.ats || 0,
        item?.stockCoverDays || 0,
        item?.oosDays || 0,
        item?.daysInStock || 0,
        item?.revenueLoss || 0,
        item?.lastRestockDate || "N/A",
        item?.lastRestockQty || 0,
        item?.orderNeeded || 0,
      ];

      let fakedProgress = 0;

      const updateToastProgress = (processed: number) => {
        let currentProgress = total
          ? Math.min((processed / total) * 100, 99.9)
          : Math.min(fakedProgress + (100 - fakedProgress) * 0.05, 95);
        fakedProgress = currentProgress;

        toast.update(toastId, {
          render: (
            <div>
              <div className="mb-2 d-flex justify-content-between fs-13 fw-bold text-gray-800">
                <span>Exporting: {processed.toLocaleString()} records</span>
                {total ? <span>{Math.round(currentProgress)}%</span> : null}
              </div>
              <ProgressBar
                now={currentProgress}
                variant="success"
                className="h-5px"
              />
            </div>
          ),
          type: "info",
          isLoading: false,
          autoClose: false,
        });
      };

      const initialRecords = countResponse?.records || [];
      if (initialRecords.length > 0) {
        const mappedRows = initialRecords.map(rowMapper);
        worksheetData.push(...mappedRows);

        updateToastProgress(worksheetData.length - 1);
      }

      let currentPage = 2;
      let hasMore = initialRecords.length > 0;

      while (hasMore) {
        if (runId !== exportRef.current) {
          toast.dismiss(toastId);
          return;
        }

        let chunkParams = {
          ...sanitizedParams,
          skip: (currentPage - 1) * EXPORT_LIMIT,
          limit: EXPORT_LIMIT,
        };

        let chunkApiService = new APICallService(
          reports.outOfStockReport,
          chunkParams,
          "",
          "",
          "",
          "",
          OutOfStockReportPermission,
          selectedWarehouse?.value,
        );

        let chunkResponse = await chunkApiService.callAPI();

        if (runId !== exportRef.current) return;

        if (!chunkResponse || chunkResponse === 0) {
          throw new Error(
            "Connection lost or API error occurred during export.",
          );
        }

        const records = chunkResponse?.records || [];

        if (records.length > 0) {
          const mappedRows = records.map(rowMapper);
          worksheetData.push(...mappedRows);

          updateToastProgress(worksheetData.length - 1);
        }

        if (!records || records.length === 0) {
          hasMore = false;
        } else {
          hasMore = true;
          currentPage++;
        }

        if (currentPage > 20000) hasMore = false;
      }

      if (runId !== exportRef.current) return;

      toast.update(toastId, {
        render: (
          <div>
            <div className="mb-2 d-flex justify-content-between fs-13 fw-bold text-gray-800">
              <span>Finalizing Excel file...</span>
              <span>100%</span>
            </div>
            <ProgressBar now={100} variant="success" className="h-5px" />
          </div>
        ),
        type: "info",
        isLoading: false,
        autoClose: false,
      });

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      worksheet["!cols"] = [
        { width: 35 },
        { width: 20 },
        { width: 20 },
        { width: 20 },
        { width: 15 },
        { width: 20 },
        { width: 12 },
        { width: 18 },
        { width: 12 },
        { width: 18 },
        { width: 12 },
        { width: 18 },
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, "ATS Report");
      XLSX.writeFile(
        workbook,
        `ats-report-${new Date().toISOString().split("T")[0]}.xlsx`,
      );

      toast.dismiss(toastId);
      toast.success("Export completed successfully!", {
        autoClose: false,
        closeOnClick: false,
        className: "toast-center-icon",
      });
    } catch (error) {
      if (runId === exportRef.current) {
        console.error("Export error:", error);
        toast.dismiss(toastId);
        toast.error("Export failed: " + (error as Error).message, {
          autoClose: false,
          closeOnClick: false,
          className: "toast-center-icon",
        });
      }
    } finally {
      if (runId === exportRef.current) {
        setIsExporting(false);
      }
    }
  };

  return (
    <>
      <div className="p-5">
        <Row className="align-items-center mb-5">
          <Col sm>
            <h1 className="fs-22 fw-bolder mb-0">ATS Report</h1>
          </Col>
          <Col xs="auto">
            {isAllowedToDownload && (
              <Button
                data-analytics-event="finance_reports_out_of_stock_report_download_excel_clicked"
                variant="success"
                onClick={handleExportExcel}
                className="uw-btn uw-btn-success me-3"
                disabled={loading || isExporting}
              >
                <KTSVG
                  path="/media/icons/duotune/files/fil003.svg"
                  className="svg-icon-2 me-2"
                />
                Download Excel
              </Button>
            )}
          </Col>
        </Row>
        {showPermissionModal && (
          <PermissionModal
            show={showPermissionModal}
            onHide={() => setShowPermissionModal(false)}
            moduleName={"orders & delivery"}
          />
        )}
        {/* Search Bar */}
        <Card className="border bg-light mb-4">
          <Card.Body className="px-7">
            <Row className="gx-5 gy-4 out-of-stock-filters">
              {/* Row 1 */}
              <Col xl={4} lg={4} md={6}>
                <div className="d-flex align-items-center position-relative h-60px search-input-wrapper">
                  <KTSVG
                    path="/media/icons/duotune/general/gen021.svg"
                    className="svg-icon-3 position-absolute ms-5"
                  />
                  <input
                    type="text"
                    value={searchTerm}
                    className="form-control bg-white ps-13 text-dark h-100 fs-14 fw-600"
                    placeholder="Search by product and vendor"
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>
              </Col>
              <Col xl={4} lg={4} md={6}>
                <CustomSelect
                  placeholder="Filter by Category"
                  backgroundColor="#fff"
                  value={selectedCategory}
                  isLoading={fetchCategoryLoader}
                  disabled={loading}
                  options={categories}
                  onChange={handleCategoryFilter}
                  onMenuScrollToBottom={() => {
                    if (categories.length < totalCategory) {
                      const nextPage = categoryPage + 1;
                      setCategoryPage(nextPage);
                      fetchCategory(nextPage, PAGE_LIMIT, categorySearch);
                    }
                  }}
                  onInputChange={(val: string, { action }: any) => {
                    if (action === "input-change") {
                      setCategorySearch(val);
                      setCategoryPage(1);
                      fetchCategory(1, PAGE_LIMIT, val);
                    }
                  }}
                  onMenuOpen={() => {
                    setCategoryPage(1);
                    fetchCategory(1, PAGE_LIMIT, categorySearch);
                  }}
                  onMenuClose={() => {
                    setCategorySearch("");
                  }}
                  isClearable={true}
                />
              </Col>
              <Col xl={4} lg={4} md={6}>
                <CustomSelect
                  placeholder="Filter by Sub Category"
                  backgroundColor="#fff"
                  value={selectedSubCategory}
                  options={subCategories}
                  onChange={handleSubCategoryFilter}
                  isClearable={true}
                  disabled={!selectedCategory || loading}
                />
              </Col>
              <Col xl={4} lg={4} md={6}>
                <CustomSelect
                  placeholder="Filter by Group Category"
                  backgroundColor="#fff"
                  value={selectedGroupCategory}
                  options={groupCategories}
                  onChange={handleGroupCategoryFilter}
                  isClearable={true}
                  disabled={!selectedSubCategory || loading}
                />
              </Col>
              {/* Row 2 */}
              <Col xl={4} lg={4} md={6}>
                <CustomSelect
                  placeholder="Filter by Brand"
                  backgroundColor="#fff"
                  isClearable={true}
                  isLoading={fetchBrandLoader}
                  options={brands}
                  value={selectedBrand}
                  onChange={handleBrandFilter}
                  disabled={loading}
                  onMenuScrollToBottom={() => {
                    if (brands.length < totalBrand) {
                      const nextPage = brandPage + 1;
                      setBrandPage(nextPage);
                      fetchBrands(nextPage, PAGE_LIMIT, brandSearch);
                    }
                  }}
                  onInputChange={(val: string, { action }: any) => {
                    if (action === "input-change") {
                      setBrandSearch(val);
                      setBrandPage(1);
                      fetchBrands(1, PAGE_LIMIT, val);
                    }
                  }}
                  onMenuOpen={() => {
                    setBrandPage(1);
                    fetchBrands(1, PAGE_LIMIT, brandSearch);
                  }}
                  onMenuClose={() => {
                    setBrandSearch("");
                  }}
                />
              </Col>
              <Col xl={4} lg={4} md={6}>
                <div className="date-picker-wrapper-60px">
                  <CustomDatePicker
                    className="form-control bg-white fs-14 fw-600 text-dark w-100"
                    onChange={handleDateChange}
                    selectsRange={true}
                    startDate={startDate}
                    endDate={endDate}
                    placeholderText="Filter by Date Range"
                    dateFormat="dd/MM/yyyy"
                    maxDate={
                      startDate && !endDate
                        ? (() => {
                            const d = new Date(startDate);
                            d.setMonth(d.getMonth() + 3);
                            const today = new Date();
                            return d > today ? today : d;
                          })()
                        : new Date()
                    }
                    isClearable={true}
                    inputTextBG="bg-white"
                    disabled={loading}
                  />
                </div>
              </Col>
            </Row>
            <style>{`
              .toast-center-icon .Toastify__close-button {
                position: absolute !important;
                right: 12px !important;
                top: 50% !important;
                transform: translateY(-50%) !important;
              }
              .out-of-stock-filters .search-input-wrapper input,
              .out-of-stock-filters .date-picker-wrapper-60px .react-datepicker-wrapper,
              .out-of-stock-filters .date-picker-wrapper-60px .react-datepicker__input-container,
              .out-of-stock-filters .date-picker-wrapper-60px .react-datepicker__input-container input {
                height: 60px !important;
                min-height: 60px !important;
                font-size: 14px !important;
              }
              /* Target React Select to match height and fix the column overflow spacing issue */
              .out-of-stock-filters div[class*="-control"] {
                min-height: 60px !important;
                height: 60px !important;
                min-width: 100% !important;
                width: 100% !important;
                padding: 0 10px !important;
              }
            `}</style>
          </Card.Body>
        </Card>
        <div className="table-responsive border rounded">
          {!fetchLoading && !loading && reportData?.length === 0 ? (
            <div className="text-center py-7">
              <div className="fs-16 text-muted">No data found</div>
            </div>
          ) : (
            <table
              className="table table-rounded table-row-bordered align-middle gs-7 gy-4 mb-0"
              style={{ tableLayout: "auto" }}
            >
              <thead>
                <tr className="fs-16 fw-600 h-65px align-middle">
                  <th className="min-w-200px">Product</th>
                  <th className="min-w-120px text-center">Qty Type</th>
                  <th className="min-w-120px text-center">Blocked Stock</th>
                  <th className="min-w-150px text-center">
                    Total Warehouse Stock
                  </th>
                  <th className="min-w-120px text-center">Qty Sold</th>
                  <th className="min-w-150px text-center">Sales Value</th>
                  <th className="min-w-100px text-center">ATS</th>
                  <th className="min-w-150px text-center">Stock Cover Days</th>
                  <th className="min-w-120px text-center">OOS Days</th>
                  <th className="min-w-150px text-center">Days in stock</th>
                  <th className="min-w-150px text-center">Revenue Loss</th>
                  <th className="min-w-150px text-center">Last Restock Date</th>
                  <th className="min-w-150px text-center">Last Restock Qty</th>
                  <th className="min-w-150px text-center">Order Needed</th>
                  <th className="min-w-150px text-center">Vendor Name (PO)</th>
                  <th className="min-w-150px text-center">
                    ATS Stock Count (PO)
                  </th>
                  <th className="min-w-150px text-center">
                    Purchase Amount (PO)
                  </th>
                </tr>
              </thead>
              <tbody style={{ backgroundColor: "#ffff" }}>
                {loading || fetchLoading ? (
                  <tr>
                    <td colSpan={16} className="text-center py-7">
                      <Spinner animation="border" variant="primary" />
                    </td>
                  </tr>
                ) : (
                  reportData?.map((val: any, index: any) => (
                    <tr key={val?._id}>
                      <td>
                        <div className="d-flex flex-column">
                          <span className="fs-15 fw-700 text-dark">
                            {val?.title}
                          </span>
                          <span className="fs-13 fw-500 text-muted">
                            {(typeof val?.brand === "object"
                              ? val?.brand?.title
                              : val?.brand) || "N/A"}{" "}
                            |{" "}
                            {(typeof val?.category === "object"
                              ? val?.category?.title
                              : val?.category) || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="text-center">
                        <span className="fs-15 fw-600">
                          {getQtyLabel(val?.type)}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="fs-15 fw-600">
                          {val?.blockedStock || 0}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="fs-15 fw-600">
                          {val?.totalWarehouseStock || 0}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="fs-15 fw-600">
                          {val?.qtySold || 0}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="fs-15 fw-600">
                          {"TSh " +
                            Method.getGeneralizedAmount(val?.salesValue || 0)}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="fs-15 fw-600">{val?.ats || 0}</span>
                      </td>
                      <td className="text-center">
                        <span className="fs-15 fw-600">
                          {val?.stockCoverDays || 0}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="fs-15 fw-600">
                          {val?.oosDays || 0}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="fs-15 fw-600">{val?.daysInStock}</span>
                      </td>
                      <td className="text-center">
                        <span className="fs-15 fw-600 text-danger">
                          {"TSh " +
                            Method.getGeneralizedAmount(val?.revenueLoss || 0)}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="fs-15 fw-600">
                          {val?.lastRestockDate || "N/A"}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="fs-15 fw-600">
                          {val?.lastRestockQty || 0}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="fs-15 fw-600">
                          {val?.orderNeeded || 0}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="fs-15 fw-600">
                          {val?.VendorName || "N/A"}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="fs-15 fw-600">
                          {val?.StockCount || 0}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="fs-15 fw-600">
                          {val?.PurchaseAmount || 0}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {reportData?.length > 0 && (
          <SimplePagination
            currentPage={page}
            onPageChange={handleCurrentPage}
            hasMore={
              totalRecords > 0
                ? page * pageLimit < totalRecords
                : reportData.length >= pageLimit
            }
            loading={loading}
          />
        )}
      </div>
    </>
  );
};

export default OutOfStockReport;
